import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import {
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  selectOrg as apiSelectOrg,
  getMe,
} from '../api/auth'
import { clearAuthTokens, onAuthSessionExpired } from '../auth/session'
import type { AuthResponse, UserResponse } from '../types/api'
import { resolveOrgSlug, tokenKey } from '../utils/org-slug'

interface AuthContextType {
  user: UserResponse | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (emailOrUsername: string, password: string) => Promise<AuthResponse>
  selectOrg: (selectToken: string, orgSlug: string) => Promise<void>
  register: (email: string, username: string, password: string) => Promise<AuthResponse>
  /** Adopts tokens already issued elsewhere (the OAuth callback redirect) instead of a password exchange. */
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

/** Token keys pinned to an explicit org slug (bare-login path has no slug in the URL yet). */
function orgTokenKey(base: string, slug: string): string {
  return `${base}:${slug}`
}

function storeTokensForOrg(slug: string, accessToken: string, refreshToken: string): void {
  localStorage.setItem(orgTokenKey('accessToken', slug), accessToken)
  localStorage.setItem(orgTokenKey('refreshToken', slug), refreshToken)
  localStorage.removeItem('token')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    const accessToken = localStorage.getItem(tokenKey('accessToken'))
    // Support legacy token migration
    const legacyToken = localStorage.getItem('token')
    if (!accessToken && !legacyToken) {
      setIsLoading(false)
      return
    }

    // Migrate legacy token
    if (!accessToken && legacyToken) {
      localStorage.setItem(tokenKey('accessToken'), legacyToken)
      localStorage.removeItem('token')
    }

    try {
      const userData = await getMe()
      setUser(userData)
    } catch {
      clearAuthTokens()
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    return onAuthSessionExpired(() => {
      setUser(null)
    })
  }, [])

  const login = async (emailOrUsername: string, password: string): Promise<AuthResponse> => {
    const response = await apiLogin(emailOrUsername, password)

    // Ambiguous bare login (one password, >1 org row): no tokens yet — the
    // caller routes to /select-org, which loads the options and calls selectOrg().
    if (response.requiresOrgSelection) {
      return response
    }

    if (!response.accessToken || !response.refreshToken) {
      throw new Error('Login failed')
    }

    // Bare entry (`/login`, no slug in URL): the backend resolved the org.
    // Pin tokens to that org and hard-navigate so the router basename resets
    // to `/{slug}`; the destination page's AuthProvider completes getMe().
    if (resolveOrgSlug() === undefined && response.orgSlug) {
      storeTokensForOrg(response.orgSlug, response.accessToken, response.refreshToken)
      window.location.href = `/${response.orgSlug}/dashboard`
      return response
    }

    localStorage.setItem(tokenKey('accessToken'), response.accessToken)
    localStorage.setItem(tokenKey('refreshToken'), response.refreshToken)
    localStorage.removeItem('token')
    const userData = await getMe()
    setUser(userData)
    return response
  }

  const selectOrg = async (selectToken: string, orgSlug: string): Promise<void> => {
    const response = await apiSelectOrg(selectToken, orgSlug)
    if (!response.accessToken || !response.refreshToken || !response.orgSlug) {
      throw new Error('Organization selection failed')
    }
    storeTokensForOrg(response.orgSlug, response.accessToken, response.refreshToken)
    window.location.href = `/${response.orgSlug}/dashboard`
  }

  const register = async (
    email: string,
    username: string,
    password: string
  ): Promise<AuthResponse> => {
    const response = await apiRegister(email, username, password)
    if (!response.accessToken || !response.refreshToken) {
      throw new Error('Registration failed')
    }
    // Bare entry (`/register`): backend created the user in the resolved org.
    if (resolveOrgSlug() === undefined && response.orgSlug) {
      storeTokensForOrg(response.orgSlug, response.accessToken, response.refreshToken)
      window.location.href = `/${response.orgSlug}/dashboard`
      return response
    }
    localStorage.setItem(tokenKey('accessToken'), response.accessToken)
    localStorage.setItem(tokenKey('refreshToken'), response.refreshToken)
    localStorage.removeItem('token')
    const userData = await getMe()
    setUser(userData)
    return response
  }

  const loginWithTokens = async (accessToken: string, refreshToken: string) => {
    localStorage.setItem(tokenKey('accessToken'), accessToken)
    localStorage.setItem(tokenKey('refreshToken'), refreshToken)
    localStorage.removeItem('token')
    const userData = await getMe()
    setUser(userData)
  }

  const logout = async () => {
    try {
      await apiLogout()
    } catch {
      // Ignore server errors on logout — still clear local state
    }
    localStorage.removeItem(tokenKey('accessToken'))
    localStorage.removeItem(tokenKey('refreshToken'))
    localStorage.removeItem('token')
    setUser(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        selectOrg,
        register,
        loginWithTokens,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
